import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';
import { Plus, Eye, Edit2, Trash2, Send, RefreshCw, Play, FileText, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import AddJobModal, { NewJobPayload as NewJobData } from 'components/jobs/AddJobModal';
import { JobListing } from 'components/personal_profile/UserExperience';
import JobDetailView from 'components/jobs/JobDetailView';
import { apiService } from 'services/apiService';
import EmptyState from 'components/common/EmptyState';
import { NoProject } from 'assets/Icons';
import deletionImage from 'assets/images/Confirm_delection.png';
import succesimage from 'assets/images/Succes_img.png';
import ModalSuccesUpdate from 'components/common/ModalSuccesUpdate';
import SkeletonLoader from 'components/ui/SkeletonLoader';
import { encryptId, decryptId } from 'utils/urlId';
import 'assets/css/JobListings.css';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import VerificationRequiredModal from 'components/ConfirmationModals/VerificationRequiredModal/VerificationRequiredModal';
import CreditConfirmPopup from 'components/payments/CreditConfirmPopup';
import { useCreditConfirm } from 'customhooks/useCreditConfirm';
import { notifyCreditChange } from 'utils/creditEvents';
import './JobManagement.css';
import CloseJobModal from './Closejobmodal';

interface FetchedSkill {
    id: number;
    name: string;
}

interface FetchedJobData {
    job_position_id: number;
    job_id?: number;
    designation_name: string;
    title?: string;
    job_title?: string;
    description: string;
    experience_text: string;
    experience_name?: string;
    posted_ago: string;
    designation_id: string;
    skills: FetchedSkill[];
    experiance_id: string;
    experience_id?: string | number;
    start_or_end?: string;
    start_date?: string;
    end_date?: string;
    distance?: string | number;
    hiring?: string | number;
    days_ago?: string;
    created_at?: string;
    type?: string;
    industry?: string;
    save_job?: boolean;
    package?: string;
    package_name?: string;
    company_name?: string;
    matching_score?: string;
    about_company?: string;
    resume?: string;
    saveJob?: boolean;
    companyName?: string;
    matchingScore?: string;
    aboutCompany?: string;
    packageValue?: string;
    salary_range?: string;
    salary_min?: number | null;
    salary_max?: number | null;
    salary_currency?: string;
    salary_period?: string;
    work_mode?: string;
    industries?: string[];
    is_active?: number;
    hiring_status?: string;
    location_text?: string;
    work_experience_years?: number;
    work_experience_months?: number;
    employment_type_ids?: number[];
    job_type?: { id: number; name: string }[];
    job_types?: { id: number; name: string }[];
    responsibilities?: string[];
    requirements?: string[];
    benefits?: string[];
    company_benefits?: string[];
    job_benefits_override?: string[];
    benefits_override?: number | boolean;
    perks?: string[];
    id?: number | string;
    is_closed?: boolean;
    is_draft?: boolean;
    is_paid?: boolean;
    openings_count?: number | null;
    industry_tags?: string[];
    department?: string;
    qualification?: string[];
    gender_preferences?: string[];
    language_requirements: string[];
    location?: string;
    application_deadline: string;
    age_min?: number | null;
    age_max?: number | null;
    application_count?: number;
    applicants_count?: number;
    screening_questions?: {
        id?: string;
        text: string;
        options?: string[];
        mandatory?: boolean;
        type?: 'text' | 'single_choice' | 'multiple_choice' | 'yes_no';
    }[];
}

interface JobSummary {
    total_jobs?: number;
    active_jobs?: number;
    draft_jobs?: number;
    closed_jobs?: number;
    expired_jobs?: number;
}

interface ApiResponse {
    data?: {
        jobs?: FetchedJobData[];
        summary?: JobSummary;
    } | FetchedJobData[];
}

const MetaIcon = ({ children }: { children: React.ReactNode }) => (
    <span className="jl-meta-icon">{children}</span>
);

const BriefcaseIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </svg>
);

const PersonIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21a8 8 0 0 0-16 0" />
        <circle cx="12" cy="8" r="4" />
    </svg>
);

const ClockIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
    </svg>
);

const BuildingIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="3" width="16" height="18" rx="2" />
        <path d="M9 8h.01M9 12h.01M9 16h.01M15 8h.01M15 12h.01M15 16h.01" />
    </svg>
);

const shimmerStyle: React.CSSProperties = {
    background: 'linear-gradient(90deg, #e8edf5 25%, #f4f6fb 50%, #e8edf5 75%)',
    backgroundSize: '200% 100%',
    animation: 'lf-shimmer 1.4s infinite',
    borderRadius: 4,
};

const SkeletonJobCard: React.FC = () => (
    <>
        <style>{`
            @keyframes lf-shimmer {
                0%   { background-position: 200% 0; }
                100% { background-position: -200% 0; }
            }
        `}</style>
        <div className="jl-card jl-card--skeleton">
            <div className="jl-card__content">
                <div className="jl-card__main">
                    <div className="jl-card__titleRow" style={{ gap: 10 }}>
                        <div style={{ ...shimmerStyle, width: '45%', height: 18 }} />
                        <div style={{ ...shimmerStyle, width: 70, height: 18 }} />
                    </div>
                    <div className="jl-card__meta" style={{ marginTop: 8, gap: 8, display: 'flex', flexWrap: 'wrap' }}>
                        <div style={{ ...shimmerStyle, width: '35%', height: 14 }} />
                        <div style={{ ...shimmerStyle, width: '25%', height: 14 }} />
                    </div>
                    <div className="jl-card__tags" style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <div style={{ ...shimmerStyle, width: 65, height: 20 }} />
                        <div style={{ ...shimmerStyle, width: 50, height: 20 }} />
                        <div style={{ ...shimmerStyle, width: 55, height: 20 }} />
                    </div>
                </div>
                <div className="jl-card__actions" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <div style={{ ...shimmerStyle, width: 55, height: 28, borderRadius: 6 }} />
                    <div style={{ ...shimmerStyle, width: 55, height: 28, borderRadius: 6 }} />
                    <div style={{ ...shimmerStyle, width: 75, height: 28, borderRadius: 6 }} />
                    <div style={{ ...shimmerStyle, width: 55, height: 28, borderRadius: 6 }} />
                </div>
            </div>
        </div>
    </>
);

const JobCard: React.FC<{
    job: JobListing;
    isSelected?: boolean;
    hasSelection?: boolean;
    onSelectToggle?: (jobId: number) => void;
    onView: (job: JobListing) => void;
    onEdit: (job: JobListing) => void;
    onDelete: (job: JobListing) => void;
    onToggleStatus: (job: JobListing) => void;
}> = ({ job, isSelected = false, hasSelection = false, onSelectToggle, onView, onEdit, onDelete, onToggleStatus }) => {
    const visibleSkills = job.skills?.slice(0, 4) || [];

    return (
        <div
            className={`jl-card ${isSelected ? 'jl-card--selected' : ''}`}
            onClick={() => onView(job)}
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
        >
            <div className="jl-card__content">
                <div className="jl-card__main">
                    <div className="jl-card__titleRow" style={{ justifyContent: 'space-between', width: '100%' }}>
                        <div className="d-flex align-items-center flex-wrap jl-card__headerLeft" style={{ gap: 8 }}>
                            {onSelectToggle && (
                                <input
                                    type="checkbox"
                                    className="jl-checkbox-custom"
                                    checked={isSelected}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        onSelectToggle(job.id);
                                    }}
                                    title="Select job for bulk action"
                                />
                            )}
                            <h3 className="jl-card__title">
                                {job.title}
                            </h3>
                            <span
                                className={`jl-badge-status ${(job as any).is_draft ? 'jl-badge-status--draft' : job.isClosed ? 'jl-badge-status--closed' : 'jl-badge-status--active'}`}
                                style={{
                                    background: (job as any).is_draft ? '#f3e8ff' : job.isClosed ? '#fee2e2' : '#dcfce7',
                                    color: (job as any).is_draft ? '#7e22ce' : job.isClosed ? '#dc2626' : '#15803d',
                                    border: `1px solid ${(job as any).is_draft ? '#e9d5ff' : job.isClosed ? '#fca5a5' : '#bbf7d0'}`,
                                }}
                            >
                                <span
                                    className="jl-badge-dot"
                                    style={{
                                        background: (job as any).is_draft ? '#9333ea' : job.isClosed ? '#ef4444' : '#16a34a',
                                    }}
                                />
                                {(job as any).is_draft ? 'Draft' : job.isClosed ? 'Closed' : 'Active'}
                            </span>
                            <span
                                className={`jl-badge-paid ${(job as any).is_paid ? 'jl-badge-paid--paid' : 'jl-badge-paid--unpaid'}`}
                                style={{
                                    background: (job as any).is_paid ? '#EFF6FF' : '#F9FAFB',
                                    color: (job as any).is_paid ? '#1D4ED8' : '#6B7280',
                                    border: `1px solid ${(job as any).is_paid ? '#BFDBFE' : '#E5E7EB'}`,
                                }}
                            >
                                <span
                                    className="jl-badge-dot"
                                    style={{
                                        background: (job as any).is_paid ? '#2563EB' : '#9CA3AF',
                                    }}
                                />
                                {(job as any).is_paid ? 'Paid' : 'Unpaid'}
                            </span>
                        </div>
                    </div>

                    <div className="jl-card__meta">
                        {job.type && (
                            <span className="jl-meta-item">
                                <MetaIcon><BriefcaseIcon /></MetaIcon>
                                {job.type}
                            </span>
                        )}
                        {job.experience && (
                            <span className="jl-meta-item">
                                <MetaIcon><PersonIcon /></MetaIcon>
                                {job.experience}
                            </span>
                        )}
                        {job.package && (
                            <span className="jl-meta-item">
                                {job.package}
                            </span>
                        )}
                        {job.application_deadline && (
                            <span className="jl-meta-item" title="Application Deadline">
                                <MetaIcon><ClockIcon /></MetaIcon>
                                Deadline: {!Number.isNaN(new Date(job.application_deadline).getTime())
                                    ? new Date(job.application_deadline).toLocaleString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: true,
                                    })
                                    : "—"}
                            </span>
                        )}
                        {job.companyName && (
                            <span className="jl-meta-item">
                                <MetaIcon><BuildingIcon /></MetaIcon>
                                {job.companyName}
                            </span>
                        )}
                    </div>

                    {visibleSkills.length > 0 && (
                        <div className="jl-card__tags">
                            {visibleSkills.map((skill) => (
                                <span key={skill.id} className="jl-tag">
                                    {skill.name}
                                </span>
                            ))}
                            {job.skills.length > 4 && (
                                <span className="jl-more">+{job.skills.length - 4} more</span>
                            )}
                        </div>
                    )}
                </div>

                <div className="jl-card__actions" onClick={(e) => e.stopPropagation()}>
                    <button
                        type="button"
                        className="jl-btn jl-btn--view"
                        onClick={(e) => {
                            e.stopPropagation();
                            onView(job);
                        }}
                        disabled={hasSelection}
                    >
                        <Eye size={13} className="me-1" />
                        View
                    </button>
                    <button
                        type="button"
                        className="jl-btn jl-btn--edit"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(job);
                        }}
                        disabled={hasSelection}
                    >
                        <Edit2 size={13} className="me-1" />
                        Edit
                    </button>
                    <button
                        type="button"
                        className="jl-btn jl-btn--toggle"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleStatus(job);
                        }}
                        disabled={hasSelection}
                    >
                        {(job as any).is_draft || job.isClosed || job.is_closed ? (
                            <Play size={13} className="me-1" />
                        ) : (
                            <RefreshCw size={13} className="me-1" />
                        )}
                        {(job as any).is_draft || job.isClosed || job.is_closed ? 'Publish' : 'Move to Draft'}
                    </button>
                    <button
                        type="button"
                        className="jl-btn jl-btn--delete"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(job);
                        }}
                        disabled={hasSelection}
                    >
                        <Trash2 size={13} className="me-1" />
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

const getJobsFromResponse = (response?: ApiResponse): FetchedJobData[] => {
    if (!response?.data) return [];
    if (Array.isArray(response.data)) return response.data;
    return Array.isArray(response.data.jobs) ? response.data.jobs : [];
};

const normalizeBenefits = (value: any): { title: string; description: string; created_at?: string }[] => {
    if (!value) return [];
    const source = Array.isArray(value) ? value : [value];

    return source
        .map((item: any) => {
            if (typeof item === 'string') {
                return {
                    title: item.trim(),
                    description: '',
                    created_at: undefined,
                };
            }
            if (item && typeof item === 'object') {
                return {
                    title: String(item.title || item.name || item.label || '').trim(),
                    description: String(item.description || item.subtext || '').trim(),
                    created_at: item.created_at || undefined,
                };
            }
            return { title: '', description: '', created_at: undefined };
        })
        .filter((item) => item.title);
};

const mapApiJobToListing = (item: FetchedJobData, defaultCompanyBenefits: { title: string; description: string; created_at?: string }[] = []): JobListing => {
    const companyBenefits = normalizeBenefits(item.company_benefits).length
        ? normalizeBenefits(item.company_benefits)
        : defaultCompanyBenefits;
    const jobBenefits = normalizeBenefits(item.benefits);
    const jobOverrideBenefits = normalizeBenefits(item.job_benefits_override);
    const hasOverride =
        item.benefits_override === 1 ||
        item.benefits_override === true;

    return {
        id: Number(item.job_position_id || item.job_id || 0),
        title: item.title || item.job_title || '',
        designation_id: item.designation_id,
        experiance_id: String(item.experiance_id || item.experience_id || ''),
        experience_id: String(item.experience_id || item.experiance_id || ''),
        experience: item.experience_text || item.experience_name || '',
        posted: item.posted_ago ? `Posted ${item.posted_ago}` : '',
        description: item.description || '',
        skills: item.skills || [],
        startOrEnd:
            item.start_or_end ||
            [item.start_date, item.end_date].filter(Boolean).join(' - '),
        distance: item.distance?.toString?.() || '',
        hiring:
            item.hiring?.toString?.() ||
            item.hiring_status ||
            (item.is_active === 1 ? 'Still Hiring' : item.is_active === 0 ? 'Closed' : ''),
        daysAgo: item.days_ago || item.posted_ago,
        type: item.type || item.work_mode || '',
        industry: item.industry || item.industries?.join(', ') || (Array.isArray(item.industry_tags) ? item.industry_tags.map((t: any) => t?.name || t).join(', ') : item.industry_tags) || '',
        industry_tags: Array.isArray(item.industry_tags)
            ? item.industry_tags.map((t: any) =>
                typeof t === 'object' && t !== null ? { id: t.id ?? 0, name: t.name ?? '' } : { id: 0, name: String(t) }
            ).filter((t: any) => t.name)
            : Array.isArray(item.industries) && item.industries.length > 0
                ? item.industries.map((s: string) => ({ id: 0, name: String(s).trim() })).filter((t: any) => t.name)
                : typeof item.industry === 'string' && item.industry
                    ? item.industry.split(',').map((s: string) => ({ id: 0, name: s.trim() })).filter((t: any) => t.name)
                    : [],
        saveJob: Boolean(item.save_job ?? item.saveJob),
        package: (() => {
            if (item.salary_min != null && item.salary_max != null)
                return `${item.salary_currency || 'INR'} ${item.salary_min} - ${item.salary_max} / ${item.salary_period || 'year'}`;
            if (item.salary_min != null)
                return `${item.salary_currency || 'INR'} ${item.salary_min} / ${item.salary_period || 'year'}`;
            return item.package || item.package_name || item.packageValue || '';
        })(),
        companyName: item.company_name || item.companyName || '',
        matchingScore: item.matching_score || item.matchingScore || '',
        aboutCompany: item.about_company || item.aboutCompany || '',
        resume: item.resume || '',
        is_draft: item.is_draft ?? false,
        is_paid: Boolean(item.is_paid ?? (!item.is_draft && (item.job_position_id || item.job_id || item.id))),
        isClosed: item.is_draft ? false : (item.is_closed ?? item.is_active === 0),
        is_closed: item.is_draft ? false : (item.is_closed ?? item.is_active === 0),
        // Pre-fill edit form fields
        location_text: item.location_text || item.location || '',
        salary_min: item.salary_min ?? null,
        salary_max: item.salary_max ?? null,
        age_min: item.age_min ?? null,
        age_max: item.age_max ?? null,
        salary_currency: item.salary_currency || 'INR',
        salary_period: item.salary_period || 'year',
        work_experience_years: item.work_experience_years,
        work_experience_months: item.work_experience_months,
        applicants_count: item.applicants_count,
        employment_type_ids: item.employment_type_ids ||
            (item.job_types && Array.isArray(item.job_types)
                ? item.job_types.map((t: any) => t.id || t).filter(Boolean)
                : []) || [],
        responsibilities: item.responsibilities || [],
        requirements: item.requirements || [],
        benefits: jobBenefits.length ? jobBenefits : companyBenefits,
        company_benefits: companyBenefits,
        job_benefits_override: jobOverrideBenefits,
        benefits_override: hasOverride,
        perks: item.perks || [],
        openings_count: item.openings_count ?? null,
        work_mode: item.work_mode || item.type || '',
        department: item.department || '',
        qualification: item.qualification || [],
        gender_preferences: item.gender_preferences || [],
        language_requirements: item.language_requirements || [],
        application_deadline: item.application_deadline || '',
        screening_questions: item.screening_questions || [],
    };
};

const upsertJobListing = (currentJobs: JobListing[], nextJob: JobListing) => {
    const existingIndex = currentJobs.findIndex((job) => job.id === nextJob.id);

    if (existingIndex === -1) {
        return [nextJob, ...currentJobs];
    }

    return currentJobs.map((job) => (job.id === nextJob.id ? nextJob : job));
};

const JobManagement: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { jobId } = useParams<{ jobId?: string }>();

    const authState = useSelector((state: RootState) => state.auth);
    const isDomainVerified = Boolean(authState?.verified_domain);

    const [jobs, setJobs] = useState<JobListing[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedJob, setSelectedJob] = useState<JobListing | null>(null);
    const [viewMode, setViewMode] = useState<'list' | 'detail' | 'add'>('list');
    const [editJob, setEditJob] = useState<JobListing | null>(null);
    const [companyBenefits, setCompanyBenefits] = useState<{ title: string; description: string; created_at?: string }[]>([]);
    const [jobToDelete, setJobToDelete] = useState<JobListing | null>(null);
    const [isDeletingJob, setIsDeletingJob] = useState(false);
    const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
    const [activeFilter, setActiveFilter] = useState<'All' | 'Active' | 'Draft' | 'Closed'>('All');
    const [summary, setSummary] = useState<JobSummary | null>(null);

    const [selectedJobIds, setSelectedJobIds] = useState<number[]>([]);
    const [isBulkPublishing, setIsBulkPublishing] = useState(false);
    const [isBulkDrafting, setIsBulkDrafting] = useState(false);
    const [isBulkClosing, setIsBulkClosing] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [showBulkCloseModal, setShowBulkCloseModal] = useState(false);
    const [statusWarningModal, setStatusWarningModal] = useState<{
        show: boolean;
        title: string;
        message: string;
        jobNames?: string[];
        deadlineInfo?: string;
        expiredJobs?: any[];
        publishedJobIds?: number[];
    }>({
        show: false,
        title: '',
        message: '',
        publishedJobIds: [],
    });
    const [inlineDeadlines, setInlineDeadlines] = useState<Record<number, string>>({});
    const [isUpdatingDeadline, setIsUpdatingDeadline] = useState<number[]>([]);
    const publishQueueRef = useRef<Promise<void>>(Promise.resolve());
    const isInitialLoadedRef = useRef(false);
    const isDirtyRef = useRef(false);

    const summaryRef = useRef(summary);
    useEffect(() => { summaryRef.current = summary; }, [summary]);

    const jobsRef = useRef(jobs);
    useEffect(() => { jobsRef.current = jobs; }, [jobs]);

    const activeFilterRef = useRef(activeFilter);
    useEffect(() => { activeFilterRef.current = activeFilter; }, [activeFilter]);

    const getTabCount = useCallback((filter: 'All' | 'Active' | 'Draft' | 'Closed') => {
        if (summary) {
            if (filter === 'All') return summary.total_jobs ?? 0;
            if (filter === 'Active') return summary.active_jobs ?? 0;
            if (filter === 'Draft') return summary.draft_jobs ?? 0;
            if (filter === 'Closed') return summary.closed_jobs ?? 0;
        }
        if (filter === 'All') return jobs.length;
        if (filter === 'Active') return jobs.filter((j) => !j.is_closed && !j.isClosed && !(j as any).is_draft).length;
        if (filter === 'Draft') return jobs.filter((j) => !!(j as any).is_draft).length;
        if (filter === 'Closed') return jobs.filter((j) => !((j as any).is_draft) && (j.is_closed || j.isClosed)).length;
        return 0;
    }, [summary, jobs]);

    const displayedJobs = useMemo(() => {
        if (activeFilter === 'All') return jobs;
        if (activeFilter === 'Active') return jobs.filter((j) => !j.is_closed && !j.isClosed && !(j as any).is_draft);
        if (activeFilter === 'Draft') return jobs.filter((j) => !!(j as any).is_draft);
        if (activeFilter === 'Closed') return jobs.filter((j) => !((j as any).is_draft) && (j.is_closed || j.isClosed));
        return jobs;
    }, [jobs, activeFilter]);

    const fetchSetting = useCallback(async (selectedJobId?: number | null, filterOverride?: 'All' | 'Active' | 'Draft' | 'Closed', silent = false) => {
        try {
            if (!silent) setLoading(true);
            apiService.clearCachePattern('/settings/get-looking-for');
            const filterToUse = filterOverride ?? activeFilterRef.current;
            const tabParam = filterToUse.toLowerCase();
            const response: ApiResponse | undefined = await apiService.getLookigFor({ tab: tabParam });

            let profileBenefits: { title: string; description: string; created_at?: string }[] = [];
            try {
                const profileResponse = await apiService.getProfile();
                const profileData: any =
                    (profileResponse as any)?.data?.companeyDetail ||
                    (profileResponse as any)?.data ||
                    {};
                profileBenefits = normalizeBenefits(profileData?.benefits);
                setCompanyBenefits(profileBenefits);
            } catch (profileError) {
                console.warn('Unable to fetch company profile benefits, using current fallback.', profileError);
            }

            const nextJobs = getJobsFromResponse(response).map((item) =>
                mapApiJobToListing(item, profileBenefits),
            );

            const rawSummary: JobSummary | undefined =
                (response as any)?.summary ||
                (response as any)?.data?.summary ||
                (response?.data && !Array.isArray(response.data) ? (response.data as any).summary : undefined);

            if (rawSummary) {
                setSummary(rawSummary);
            } else {
                setSummary((prevSummary) => {
                    const activeCount = nextJobs.filter((j) => !j.is_closed && !j.isClosed && !(j as any).is_draft).length;
                    const draftCount = nextJobs.filter((j) => !!(j as any).is_draft).length;
                    const closedCount = nextJobs.filter((j) => !((j as any).is_draft) && (j.is_closed || j.isClosed)).length;
                    const totalCount = nextJobs.length;

                    if (filterToUse === 'All') {
                        return {
                            total_jobs: totalCount,
                            active_jobs: activeCount,
                            draft_jobs: draftCount,
                            closed_jobs: closedCount,
                        };
                    }

                    return {
                        total_jobs: prevSummary?.total_jobs ?? totalCount,
                        active_jobs: filterToUse === 'Active' ? totalCount : (prevSummary?.active_jobs ?? activeCount),
                        draft_jobs: filterToUse === 'Draft' ? totalCount : (prevSummary?.draft_jobs ?? draftCount),
                        closed_jobs: filterToUse === 'Closed' ? totalCount : (prevSummary?.closed_jobs ?? closedCount),
                    };
                });
            }

            setJobs((prev) => nextJobs.map((job) => {
                const existing = prev.find((item) => item.id === job.id);
                return {
                    ...job,
                    package: job.package || existing?.package || '',
                };
            }));

            if (selectedJobId != null) {
                const nextSelectedJob = nextJobs.find((job) => job.id === selectedJobId) || null;
                setSelectedJob(nextSelectedJob);
                if (!nextSelectedJob) {
                    setViewMode((currentView) => (currentView === 'detail' ? 'list' : currentView));
                }
            }
            isDirtyRef.current = false;
        } catch (error) {
            console.error('Error fetching profiles:', error);
            if (!silent) setJobs([]);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    const companyBenefitsRef = useRef(companyBenefits);
    useEffect(() => {
        companyBenefitsRef.current = companyBenefits;
    }, [companyBenefits]);

    const fetchJobById = useCallback(async (id: string | number) => {
        try {
            setLoading(true);
            const response = await apiService.getLookingForById(id);
            let rawJob = response?.data || response;
            if (rawJob && typeof rawJob === 'object' && 'data' in rawJob && rawJob.data) {
                rawJob = rawJob.data;
            }
            if (rawJob && (rawJob.job_position_id || rawJob.id || rawJob.job_id)) {
                return mapApiJobToListing(rawJob, companyBenefitsRef.current);
            }
            return null;
        } catch (err) {
            console.error('Failed to fetch job by ID:', err);
            toast.error('Unable to fetch job details');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const {
        isOpen: isCreditPopupOpen,
        popupOptions: creditPopupOptions,
        requestConfirm,
        handleConfirm: handleCreditConfirm,
        handleCancel: handleCreditCancel,
        handleNeverShowAgain: handleCreditNeverShowAgain,
    } = useCreditConfirm();

    const [isToggling, setIsToggling] = useState(false);
    const [jobToToggle, setJobToToggle] = useState<JobListing | null>(null);
    const [jobToClose, setJobToClose] = useState<JobListing | null>(null);
    const [isClosing, setIsClosing] = useState(false);

    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const isDeadlinePassed = (job: JobListing): boolean => {
        if (!job.application_deadline) return false;
        const deadlineDate = new Date(job.application_deadline);
        if (isNaN(deadlineDate.getTime())) return false;

        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const deadlineStart = new Date(
            deadlineDate.getFullYear(),
            deadlineDate.getMonth(),
            deadlineDate.getDate()
        ).getTime();

        return deadlineStart < todayStart;
    };

    const isJobIncomplete = (job: JobListing): boolean => {
        return !job.description || !job.description.trim();
    };

    const handleToggleStatus = (job: JobListing) => {
        const isClosed = Boolean(job.is_closed || job.isClosed);
        const isDraft = Boolean((job as any).is_draft);
        const isPublishing = isDraft || isClosed;
        const deadlinePassed = isDeadlinePassed(job);

        if (isPublishing && isJobIncomplete(job)) {
            toast.error("Complete all steps before publishing job");
            return;
        }

        if (isPublishing && !isDomainVerified) {
            setShowVerificationModal(true);
            return;
        }

        if (isPublishing && deadlinePassed) {
            const deadlineText = job.application_deadline
                ? new Date(job.application_deadline).toLocaleString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                })
                : null;

            setStatusWarningModal({
                show: true,
                title: 'Cannot Publish Closed Job',
                message: `The job position "${job.title}" cannot be published directly because its application deadline has already passed. Please edit the position to extend the required application deadline before publishing.`,
                jobNames: [`"${job.title}"`],
                deadlineInfo: deadlineText
                    ? `Job Application Deadline Passed On: ${deadlineText}`
                    : `Job Application Deadline Has Passed`,
                expiredJobs: [job],
            });
            return;
        }

        setJobToToggle(job);
    };

    const handleClosePosition = (job: JobListing) => {
        const isClosed = Boolean(job.is_closed || job.isClosed);
        if (isClosed) {
            setStatusWarningModal({
                show: true,
                title: 'Job Already Closed',
                message: `The job position "${job.title}" is already closed.`,
                jobNames: [`"${job.title}"`],
            });
            return;
        }
        setJobToClose(job);
    };

    const updateSummaryOnStatusChange = (fromStatus: 'draft' | 'active' | 'closed', toStatus: 'draft' | 'active' | 'closed', count = 1) => {
        setSummary((prev) => {
            if (!prev) return prev;
            const next = { ...prev };
            if (fromStatus === 'draft') next.draft_jobs = Math.max(0, (next.draft_jobs ?? 0) - count);
            else if (fromStatus === 'active') next.active_jobs = Math.max(0, (next.active_jobs ?? 0) - count);
            else if (fromStatus === 'closed') next.closed_jobs = Math.max(0, (next.closed_jobs ?? 0) - count);

            if (toStatus === 'draft') next.draft_jobs = (next.draft_jobs ?? 0) + count;
            else if (toStatus === 'active') next.active_jobs = (next.active_jobs ?? 0) + count;
            else if (toStatus === 'closed') next.closed_jobs = (next.closed_jobs ?? 0) + count;

            return next;
        });
    };

    const updateSummaryOnDelete = (jobsToDelete: JobListing[]) => {
        setSummary((prev) => {
            if (!prev) return prev;
            const next = { ...prev };
            jobsToDelete.forEach((j) => {
                next.total_jobs = Math.max(0, (next.total_jobs ?? 0) - 1);
                if ((j as any).is_draft) next.draft_jobs = Math.max(0, (next.draft_jobs ?? 0) - 1);
                else if (j.is_closed || j.isClosed) next.closed_jobs = Math.max(0, (next.closed_jobs ?? 0) - 1);
                else next.active_jobs = Math.max(0, (next.active_jobs ?? 0) - 1);
            });
            return next;
        });
    };

    const handleConfirmClose = async () => {
        if (!jobToClose) return;
        const targetJob = jobToClose;
        setJobToClose(null);

        try {
            setIsClosing(true);
            const res = await apiService.toggleJobStatus(targetJob.id, { action: 'close' });

            if (res?.status) {
                const wasDraft = Boolean((targetJob as any).is_draft);
                const fromStatus = wasDraft ? 'draft' : 'active';

                setJobs((prev) => {
                    const updated = prev.map((j) =>
                        j.id === targetJob.id
                            ? { ...j, is_closed: true, isClosed: true, is_draft: false }
                            : j
                    );
                    if (activeFilter === 'Active' || activeFilter === 'Draft') {
                        return updated.filter((j) => j.id !== targetJob.id);
                    }
                    return updated;
                });

                updateSummaryOnStatusChange(fromStatus, 'closed', 1);
                isDirtyRef.current = true;
                toast.success('Position closed successfully');
                fetchSetting(null, undefined, true);
            } else {
                toast.error(res?.message || 'Failed to close position');
            }
        } catch (err) {
            toast.error('Error closing position');
        } finally {
            setIsClosing(false);
        }
    };

    const handleConfirmToggle = async () => {
        if (!jobToToggle) return;
        const targetJob = jobToToggle;
        setJobToToggle(null);
        const isCurrentlyClosed = Boolean(targetJob.is_closed || targetJob.isClosed);
        const isActivating = Boolean((targetJob as any).is_draft || isCurrentlyClosed);
        const action = isActivating ? 'publish' : 'draft';
        const isAlreadyPaid = Boolean(targetJob.is_paid ?? (targetJob as any).isPaid);

        if (action === 'publish' && isJobIncomplete(targetJob)) {
            toast.error("Complete all steps before publishing job");
            return;
        }

        if (isActivating && !isAlreadyPaid) {
            const confirmed = await requestConfirm({
                moduleKey: 'post_job',
                moduleName: 'Publish Job',
                featureKey: 'JOB_POST',
                creditCost: 5,
                actionDescription: 'Publishing this job position will deduct 5 credits from your balance.',
            });
            if (!confirmed) return;
        }

        try {
            setIsToggling(true);
            const res = action === 'publish'
                ? await apiService.bulkPublishJobs([targetJob.id])
                : await apiService.toggleJobStatus(targetJob.id, { action });

            if (res?.status) {
                if (action === 'publish') {
                    notifyCreditChange();
                }
                setJobs((prev) => {
                    const updated = prev.map((j) =>
                        j.id === targetJob.id
                            ? { ...j, is_draft: !isActivating, is_closed: false, isClosed: false, is_paid: isActivating ? true : j.is_paid }
                            : j
                    );
                    if (activeFilter === 'Draft' && isActivating) {
                        return updated.filter((j) => j.id !== targetJob.id);
                    }
                    if (activeFilter === 'Closed' && isActivating) {
                        return updated.filter((j) => j.id !== targetJob.id);
                    }
                    if (activeFilter === 'Active' && !isActivating) {
                        return updated.filter((j) => j.id !== targetJob.id);
                    }
                    return updated;
                });

                updateSummaryOnStatusChange(
                    isCurrentlyClosed ? 'closed' : ((targetJob as any).is_draft ? 'draft' : 'active'),
                    isActivating ? 'active' : 'draft',
                    1
                );

                isDirtyRef.current = true;
                toast.success(
                    isActivating
                        ? 'Job published successfully 🚀'
                        : 'Job moved to Draft'
                );

                fetchSetting(null, undefined, true);
            } else {
                toast.error(res?.message || 'Failed to update status');
            }
        } catch (err) {
            toast.error('Error updating job status');
        } finally {
            setIsToggling(false);
        }
    };

    const handleUpdateDeadlineAndPublish = async (job: JobListing) => {
        setIsUpdatingDeadline(prev => [...prev, job.id]);

        publishQueueRef.current = publishQueueRef.current.then(async () => {
            const newDeadline = inlineDeadlines[job.id];
            if (!newDeadline) {
                toast.error("Please select a new deadline first");
                setIsUpdatingDeadline(prev => prev.filter(id => id !== job.id));
                return;
            }

            if (isJobIncomplete(job)) {
                toast.error("Complete all steps before publishing job");
                setIsUpdatingDeadline(prev => prev.filter(id => id !== job.id));
                return;
            }

            const isAlreadyPaid = Boolean(job.is_paid ?? (job as any).isPaid);
            if (!isAlreadyPaid) {
                const confirmed = await requestConfirm({
                    moduleKey: 'post_job',
                    moduleName: 'Publish Job',
                    featureKey: 'JOB_POST',
                    creditCost: 5,
                    actionDescription: 'Publishing this job position will deduct 5 credits from your balance.',
                });
                if (!confirmed) {
                    setIsUpdatingDeadline(prev => prev.filter(id => id !== job.id));
                    return;
                }
            }

            try {
                const payload = {
                    id: job.id,
                    is_closed: false,
                    is_draft: false,
                    application_deadline: newDeadline,
                    designation_id: job.designation_id,
                    job_title: job.title,
                    employment_type_ids: job.employment_type_ids || [],
                    industry_tags: Array.isArray(job.industry_tags) ? job.industry_tags.map((t: any) => t.id || t.name || t) : [],
                    work_mode: job.work_mode || '',
                    experience_id: job.experience_id || job.experiance_id,
                    work_experience_years: job.work_experience_years || 0,
                    work_experience_months: job.work_experience_months || 0,
                    salary_min: job.salary_min ?? null,
                    salary_max: job.salary_max ?? null,
                    salary_currency: job.salary_currency || 'INR',
                    salary_period: job.salary_period || 'year',
                    salary_visibility: job.salary_min != null ? 'range' : 'hidden',
                    openings_count: job.openings_count || 1,
                    location_text: job.location_text || '',
                    location_lat: (job as any).location_lat ?? null,
                    location_lng: (job as any).location_lng ?? null,
                };
                const updateRes = await apiService.addLookingForWithStep(payload, 'basics');

                if (updateRes) {
                    const res = await apiService.bulkPublishJobs([job.id]);
                    if (res?.status || updateRes?.status) {
                        notifyCreditChange();
                        toast.success("Job deadline extended and published successfully");
                        setJobs((prev) =>
                            prev.map((j) =>
                                j.id === job.id
                                    ? { ...j, application_deadline: newDeadline, is_draft: false, is_closed: false, isClosed: false, is_paid: true }
                                    : j
                            )
                        );

                        setStatusWarningModal(prev => ({
                            ...prev,
                            publishedJobIds: [...(prev.publishedJobIds || []), job.id]
                        }));

                        isDirtyRef.current = true;
                        fetchSetting(null, undefined, true);
                    } else {
                        toast.error(res?.message || 'Failed to publish job');
                    }
                } else {
                    toast.error('Failed to update deadline');
                }
            } catch (err: any) {
                console.error(err);
                toast.error(err?.response?.data?.message || err?.message || 'Error updating deadline');
            } finally {
                setIsUpdatingDeadline(prev => prev.filter(id => id !== job.id));
            }
        }).catch(console.error);

        await publishQueueRef.current;
    };

    const handleToggleSelectJob = (jobId: number) => {
        setSelectedJobIds((prev) =>
            prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
        );
    };

    const handleToggleSelectAll = () => {
        if (selectedJobIds.length === displayedJobs.length && displayedJobs.length > 0) {
            setSelectedJobIds([]);
        } else {
            setSelectedJobIds(displayedJobs.map((j) => j.id));
        }
    };

    const [bulkToggleAction, setBulkToggleAction] = useState<'publish' | 'draft' | null>(null);

    const handleBulkPublish = async () => {
        if (!selectedJobIds.length || isBulkPublishing) return;
        if (!isDomainVerified) {
            setShowVerificationModal(true);
            return;
        }

        const selectedJobs = jobs.filter((j) => selectedJobIds.includes(j.id));
        const jobsToPublish = selectedJobs.filter((j) => (j as any).is_draft || j.is_closed || j.isClosed);
        const incompleteJobs = jobsToPublish.filter((j) => isJobIncomplete(j));

        if (incompleteJobs.length > 0) {
            toast.error("Complete all steps before publishing job");
            return;
        }

        const expiredJobs = selectedJobs.filter((j) => isDeadlinePassed(j));
        const activeJobs = selectedJobs.filter((j) => !j.is_draft && !j.is_closed && !j.isClosed);

        if (expiredJobs.length > 0) {
            const expiredNames = expiredJobs.map((j) => `"${j.title}"`);
            const countText = expiredJobs.length === 1
                ? '1 of the selected job position is'
                : `${expiredJobs.length} of the selected job position(s) are`;

            const deadlineStrings = expiredJobs
                .map((j) => {
                    if (!j.application_deadline) return null;
                    const dt = new Date(j.application_deadline).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    });
                    return expiredJobs.length > 1 ? `"${j.title}": ${dt}` : dt;
                })
                .filter(Boolean);

            const deadlineInfoText = deadlineStrings.length > 0
                ? expiredJobs.length > 1
                    ? `Job Application Deadlines Passed:\n${deadlineStrings.join('\n')}`
                    : `Job Application Deadline Passed On: ${deadlineStrings[0]}`
                : `Job Application Deadline Has Passed`;

            setStatusWarningModal({
                show: true,
                title: 'Cannot Publish Closed Job',
                message: `${countText} closed because its required application deadline has passed. Closed jobs cannot be published directly. Please edit the position to extend the required application deadline before publishing.`,
                jobNames: expiredNames,
                deadlineInfo: deadlineInfoText,
                expiredJobs: expiredJobs,
            });
            return;
        }

        if (activeJobs.length === selectedJobs.length) {
            setStatusWarningModal({
                show: true,
                title: 'Jobs Already Active',
                message: 'All selected jobs are already published and active.',
                jobNames: activeJobs.map((j) => `"${j.title}"`),
            });
            return;
        }

        setBulkToggleAction('publish');
    };

    const handleBulkDraft = async () => {
        if (!selectedJobIds.length || isBulkDrafting) return;

        const selectedJobs = jobs.filter((j) => selectedJobIds.includes(j.id));
        const draftJobs = selectedJobs.filter((j) => (j as any).is_draft);

        if (draftJobs.length === selectedJobs.length) {
            setStatusWarningModal({
                show: true,
                title: 'Jobs Already in Draft',
                message: 'All selected jobs are already saved as draft.',
                jobNames: draftJobs.map((j) => `"${j.title}"`),
            });
            return;
        }

        setBulkToggleAction('draft');
    };

    const handleConfirmBulkToggle = async () => {
        const action = bulkToggleAction;
        setBulkToggleAction(null);
        if (!action) return;

        const targetIds = [...selectedJobIds];
        const selectedJobs = jobs.filter((j) => targetIds.includes(j.id));

        if (action === 'publish') {
            const incompleteJobs = selectedJobs.filter((j) => isJobIncomplete(j));
            if (incompleteJobs.length > 0) {
                toast.error("Complete all steps before publishing job");
                return;
            }

            const unpaidJobsCount = selectedJobs.filter(j => !j.is_paid && !(j as any).isPaid).length;
            if (unpaidJobsCount > 0) {
                const confirmed = await requestConfirm({
                    moduleKey: 'post_job',
                    moduleName: 'Publish Jobs',
                    featureKey: 'JOB_POST',
                    creditCost: unpaidJobsCount * 5,
                    actionDescription: `Publishing ${unpaidJobsCount} unpaid job(s) will deduct ${unpaidJobsCount * 5} credits from your balance.`,
                });
                if (!confirmed) return;
            }

            try {
                setIsBulkPublishing(true);
                const draftCount = selectedJobs.filter(j => (j as any).is_draft).length;
                const closedCount = selectedJobs.filter(j => j.is_closed || j.isClosed).length;
                const res = await apiService.bulkPublishJobs(targetIds);

                if (res?.status) {
                    notifyCreditChange();
                    toast.success(res?.message || `${targetIds.length} job(s) published successfully.`);
                    setJobs((prev) => {
                        const updated = prev.map((j) => (targetIds.includes(j.id) ? { ...j, is_draft: false, isClosed: false, is_closed: false, is_paid: true } : j));
                        if (activeFilter === 'Draft' || activeFilter === 'Closed') {
                            return updated.filter((j) => !targetIds.includes(j.id));
                        }
                        return updated;
                    });

                    if (draftCount > 0) {
                        updateSummaryOnStatusChange('draft', 'active', draftCount);
                    }
                    if (closedCount > 0) {
                        updateSummaryOnStatusChange('closed', 'active', closedCount);
                    }

                    setSelectedJobIds([]);
                    isDirtyRef.current = true;
                    fetchSetting(null, undefined, true);
                } else {
                    toast.error(res?.message || 'Failed to publish selected jobs');
                }
            } catch (err) {
                console.error('Failed bulk publish:', err);
                toast.error('Error publishing selected jobs');
            } finally {
                setIsBulkPublishing(false);
            }
        } else {
            try {
                setIsBulkDrafting(true);
                const activeCount = selectedJobs.filter(j => !(j as any).is_draft && !j.is_closed && !j.isClosed).length;
                const closedCount = selectedJobs.filter(j => j.is_closed || j.isClosed).length;

                const draftPromises = targetIds.map(id => apiService.toggleJobStatus(id, { action: 'draft' }));
                const results = await Promise.all(draftPromises);

                const allSuccess = results.every(res => res?.status);

                if (allSuccess) {
                    toast.success(`${targetIds.length} job(s) moved to draft.`);
                    setJobs((prev) => {
                        const updated = prev.map((j) => (targetIds.includes(j.id) ? { ...j, is_draft: true, isClosed: false, is_closed: false } : j));
                        if (activeFilter === 'Active' || activeFilter === 'Closed') {
                            return updated.filter((j) => !targetIds.includes(j.id));
                        }
                        return updated;
                    });

                    if (activeCount > 0) updateSummaryOnStatusChange('active', 'draft', activeCount);
                    if (closedCount > 0) updateSummaryOnStatusChange('closed', 'draft', closedCount);

                    setSelectedJobIds([]);
                    isDirtyRef.current = true;
                    fetchSetting(null, undefined, true);
                } else {
                    toast.error('Some jobs failed to move to draft');
                    setSelectedJobIds([]);
                    fetchSetting(null, undefined, true);
                }
            } catch (err) {
                console.error('Failed bulk draft:', err);
                toast.error('Error moving selected jobs to draft');
            } finally {
                setIsBulkDrafting(false);
            }
        }
    };

    const handleOpenBulkCloseModal = () => {
        if (!selectedJobIds.length) return;

        const selectedJobs = jobs.filter((j) => selectedJobIds.includes(j.id));
        const closedJobs = selectedJobs.filter((j) => !(j as any).is_draft && (j.is_closed || j.isClosed));

        if (closedJobs.length === selectedJobs.length) {
            setStatusWarningModal({
                show: true,
                title: 'Jobs Already Closed',
                message: 'All selected job positions are already closed.',
                jobNames: closedJobs.map((j) => `"${j.title}"`),
            });
            return;
        }

        setShowBulkCloseModal(true);
    };

    const handleConfirmBulkClose = async () => {
        if (!selectedJobIds.length || isBulkClosing) return;

        try {
            setIsBulkClosing(true);
            const targetIds = [...selectedJobIds];
            const selectedJobs = jobs.filter((j) => targetIds.includes(j.id));
            const draftCount = selectedJobs.filter((j) => (j as any).is_draft).length;
            const activeCount = selectedJobs.filter((j) => !(j as any).is_draft && !j.is_closed && !j.isClosed).length;
            const res = await apiService.bulkCloseJobs(targetIds);

            if (res?.status) {
                toast.success(res?.message || `${targetIds.length} job position(s) closed.`);
                setJobs((prev) => {
                    const updated = prev.map((j) => (targetIds.includes(j.id) ? { ...j, is_closed: true, isClosed: true, is_draft: false } : j));
                    if (activeFilter === 'Active' || activeFilter === 'Draft') {
                        return updated.filter((j) => !targetIds.includes(j.id));
                    }
                    return updated;
                });

                if (draftCount > 0) updateSummaryOnStatusChange('draft', 'closed', draftCount);
                if (activeCount > 0) updateSummaryOnStatusChange('active', 'closed', activeCount);

                setSelectedJobIds([]);
                setShowBulkCloseModal(false);
                isDirtyRef.current = true;
                fetchSetting(null, undefined, true);
            } else {
                toast.error(res?.message || 'Failed to close selected job positions');
            }
        } catch (err) {
            console.error('Failed bulk close:', err);
            toast.error('Error closing selected job positions');
        } finally {
            setIsBulkClosing(false);
        }
    };

    const handleConfirmBulkDelete = async () => {
        if (!selectedJobIds.length || isBulkDeleting) return;

        try {
            setIsBulkDeleting(true);
            const targetIds = [...selectedJobIds];
            const selectedJobs = jobs.filter((j) => targetIds.includes(j.id));
            const res = await apiService.bulkDeleteJobs(targetIds);

            if (res?.status) {
                toast.success(res?.message || `${targetIds.length} job(s) deleted successfully.`);
                setJobs((prev) => prev.filter((j) => !targetIds.includes(j.id)));
                updateSummaryOnDelete(selectedJobs);
                setSelectedJobIds([]);
                setShowBulkDeleteModal(false);
                isDirtyRef.current = true;
                fetchSetting(null, undefined, true);
            } else {
                toast.error(res?.message || 'Failed to delete selected jobs');
            }
        } catch (err) {
            console.error('Failed bulk delete:', err);
            toast.error('Error deleting selected jobs');
        } finally {
            setIsBulkDeleting(false);
        }
    };

    // ── 1) Sync View Mode with URL (runs ONLY on route path/params change) ──
    useEffect(() => {
        const isAddRoute = location.pathname.endsWith('/add') || location.pathname.endsWith('/new');
        const isEditRoute = location.pathname.includes('/edit/');
        const paramId = jobId;
        const resolvedId = paramId ? (decryptId(paramId) ?? paramId) : undefined;

        if (isAddRoute) {
            setEditJob(null);
            setSelectedJob(null);
            setViewMode('add');
        } else if (isEditRoute && resolvedId) {
            fetchJobById(resolvedId).then((fetchedJob) => {
                if (fetchedJob) {
                    setEditJob(fetchedJob);
                    setSelectedJob(null);
                    setViewMode('add');
                } else {
                    navigate('/jobs', { replace: true });
                }
            });
        } else if (resolvedId && !isEditRoute) {
            fetchJobById(resolvedId).then((fetchedJob) => {
                if (fetchedJob) {
                    setSelectedJob(fetchedJob);
                    setEditJob(null);
                    setViewMode('detail');
                } else {
                    navigate('/jobs', { replace: true });
                }
            });
        }
    }, [location.pathname, jobId, fetchJobById, navigate]);

    const handleTabClick = (filter: 'All' | 'Active' | 'Draft' | 'Closed') => {
        setActiveFilter(filter);
        setSelectedJobIds([]);
        const count = getTabCount(filter);
        if (isInitialLoadedRef.current && count === 0) {
            setLoading(false);
        }
    };

    // ── 2) Fetch Job List (runs ONLY when on main list route and activeFilter changes) ──
    useEffect(() => {
        const isAddRoute = location.pathname.endsWith('/add') || location.pathname.endsWith('/new');
        const isEditRoute = location.pathname.includes('/edit/');
        const isDetailRoute = Boolean(jobId);

        if (!isAddRoute && !isEditRoute && !isDetailRoute) {
            setEditJob(null);
            setSelectedJob(null);
            setViewMode('list');

            const count = getTabCount(activeFilter);
            if (isInitialLoadedRef.current && count === 0) {
                setLoading(false);
                return;
            }

            fetchSetting(null, activeFilter).then(() => {
                isInitialLoadedRef.current = true;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, jobId, activeFilter]);

    const handleView = (job: JobListing) => {
        setSelectedJob(job);
        setViewMode('detail');
        const encrypted = encryptId(job.id);
        navigate(`/job/${encrypted || job.id}`);
    };

    const handleBack = async () => {
        setSelectedJob(null);
        setViewMode('list');
        navigate('/jobs');
    };

    const handleOpenBulkDeleteModal = () => {
        if (!selectedJobIds.length) return;
        setShowBulkDeleteModal(true);
    };

    const handleEdit = async (job: JobListing) => {
        setEditJob(job);
        setViewMode('add');
        const encrypted = encryptId(job.id);
        navigate(`/job/edit/${encrypted || job.id}`);
    };

    const handleAdd = () => {
        setEditJob(null);
        setViewMode('add');
        navigate('/job/add');
    };

    const handleDelete = (job: JobListing) => {
        setJobToDelete(job);
    };

    const handleConfirmDelete = async () => {
        if (!jobToDelete) return;
        const targetJob = jobToDelete;

        try {
            setIsDeletingJob(true);
            const res = await apiService.deleteJob(targetJob.id);
            if (res?.status) {
                setJobs((prev) => prev.filter((job) => job.id !== targetJob.id));
                setSelectedJobIds((prev) => prev.filter((id) => id !== targetJob.id));
                updateSummaryOnDelete([targetJob]);

                if (selectedJob?.id === targetJob.id) {
                    setSelectedJob(null);
                    setViewMode('list');
                    navigate('/jobs');
                }
                setJobToDelete(null);
                setShowDeleteSuccess(true);
                isDirtyRef.current = true;
                fetchSetting(null, undefined, true);
            } else {
                toast.error(res?.message || 'Failed to delete job');
            }
        } catch (error) {
            console.error('Failed to delete job:', error);
            toast.error('Failed to delete job');
        } finally {
            setIsDeletingJob(false);
        }
    };

    const handleSave = async (data: NewJobData) => {
        try {
            const saved = await apiService.addLookingFor(data);
            if (!saved?.status) {
                return saved;
            }

            const savedJobData = saved?.data && typeof saved.data === 'object'
                ? mapApiJobToListing(saved.data as FetchedJobData, companyBenefits)
                : null;

            if (savedJobData) {
                setJobs((prev) => upsertJobListing(prev, savedJobData));
                if (selectedJob?.id === savedJobData.id) {
                    setSelectedJob(savedJobData);
                }
            }

            setViewMode('list');
            setEditJob(null);
            navigate('/jobs');
            isDirtyRef.current = true;
            await fetchSetting(savedJobData?.id ?? selectedJob?.id ?? null);
            return saved;
        } catch (error) {
            console.error('Failed to save job:', error);
            return {
                status: false,
                message: 'Unable to save job post',
            };
        }
    };

    const handleDetailUpdate = (job: JobListing) => {
        setJobs((prev) => prev.map((item) => (item.id === job.id ? job : item)));
        if (selectedJob?.id === job.id) {
            setSelectedJob(job);
        }
        fetchSetting(job.id);
    };

    return (
        <div className="jl-page">
            <div className="jl-container">
                <div className="jl-panel">
                    {viewMode === 'detail' && selectedJob ? (
                        <JobDetailView
                            job={selectedJob}
                            onBack={handleBack}
                            onUpdate={handleDetailUpdate}
                            onRefresh={fetchSetting}
                            onRequestEdit={handleEdit}
                            loading={loading}
                        />
                    ) : viewMode === 'add' ? (
                        <AddJobModal
                            onCancel={async () => { setViewMode('list'); setEditJob(null); navigate('/jobs'); try { await fetchSetting(); } catch { } }}
                            handleAddJob={handleSave}
                            jobDetails={editJob}
                            companyBenefits={companyBenefits}
                        />
                    ) : (
                        <>
                            <div className="jl-top-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                                {/* Filter Tabs */}
                                <div className="jl-filter-tabs" style={{ marginBottom: 0 }}>
                                    {(['All', 'Active', 'Draft', 'Closed'] as const).map((f) => {
                                        const count = getTabCount(f);
                                        return (
                                            <button
                                                key={f}
                                                className={`jl-filter-tab${activeFilter === f ? ' jl-filter-tab--active' : ''}`}
                                                onClick={() => handleTabClick(f)}
                                            >
                                                {f}
                                                <span className="jl-filter-count">{count}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <Button variant="link" className="jl-addMore" onClick={handleAdd}>
                                    Add Job
                                    <Plus size={15} className="ms-1" />
                                </Button>
                            </div>

                            {loading ? (
                                <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'flex-start' }}>
                                    <SkeletonJobCard />
                                    <SkeletonJobCard />
                                    <SkeletonJobCard />
                                    <SkeletonJobCard />
                                </div>
                            ) : displayedJobs.length === 0 ? (
                                <EmptyState
                                    title="No Jobs Yet"
                                    description={`No ${activeFilter.toLowerCase()} jobs found.`}
                                    image={NoProject}
                                    size={120}
                                />
                            ) : (
                                <>
                                    <div className={`jl-bulk-toolbar ${selectedJobIds.length > 0 ? 'jl-bulk-toolbar--active' : ''}`}>
                                        <div className="d-flex align-items-center gap-2 jl-bulk-left">
                                            <label className="d-flex align-items-center gap-2 m-0" style={{ cursor: 'pointer', fontWeight: 600, color: '#334155', fontSize: 13 }}>
                                                <input
                                                    type="checkbox"
                                                    className="jl-checkbox-custom"
                                                    checked={displayedJobs.length > 0 && selectedJobIds.length === displayedJobs.length}
                                                    onChange={handleToggleSelectAll}
                                                />
                                                Select All ({displayedJobs.length})
                                            </label>
                                            {selectedJobIds.length > 0 && (
                                                <span className="jl-bulk-badge">
                                                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
                                                    {selectedJobIds.length} Selected
                                                </span>
                                            )}
                                        </div>

                                        {selectedJobIds.length > 0 && (
                                            <div className="d-flex align-items-center gap-2 flex-wrap jl-bulk-actions">
                                                <Button
                                                    className="jl-btn jl-btn--bulk-publish"
                                                    size="sm"
                                                    onClick={handleBulkPublish}
                                                    disabled={isBulkPublishing || isBulkDrafting || isBulkClosing || isBulkDeleting}
                                                    style={{ minWidth: 'auto',  }}
                                                >
                                                    <Send size={13} className="me-1" />
                                                    {isBulkPublishing ? 'Publishing...' : `Publish  (${selectedJobIds.length})`}
                                                </Button>
                                                <Button
                                                    className="jl-btn jl-btn--toggle"
                                                    size="sm"
                                                    onClick={handleBulkDraft}
                                                    disabled={isBulkPublishing || isBulkDrafting || isBulkClosing || isBulkDeleting}
                                                    style={{ minWidth: 'auto',  }}
                                                >
                                                    <FileText size={13} className="me-1" />
                                                    {isBulkDrafting ? 'Drafting...' : `Move to Draft (${selectedJobIds.length})`}
                                                </Button>
                                                <Button
                                                    className="jl-btn jl-btn--toggle"
                                                    size="sm"
                                                    onClick={handleOpenBulkCloseModal}
                                                    disabled={isBulkPublishing || isBulkDrafting || isBulkClosing || isBulkDeleting}
                                                    style={{ minWidth: 'auto',  }}
                                                >
                                                    <XCircle size={13} className="me-1" />
                                                    {isBulkClosing ? 'Closing...' : `Close Positions (${selectedJobIds.length})`}
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    className="jl-btn jl-btn--bulk-delete"
                                                    size="sm"
                                                    onClick={handleOpenBulkDeleteModal}
                                                    disabled={isBulkPublishing || isBulkDrafting || isBulkClosing || isBulkDeleting}
                                                    style={{ minWidth: 'auto',  }}
                                                >
                                                    <Trash2 size={13} className="me-1" />
                                                    {isBulkDeleting ? 'Deleting...' : `Delete  (${selectedJobIds.length})`}
                                                </Button>

                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    onClick={() => setSelectedJobIds([])}
                                                    style={{ fontSize: 12, color: '#64748b', textDecoration: 'none', fontWeight: 500 }}
                                                >
                                                    Deselect All
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    {displayedJobs.map((job) => (
                                        <JobCard
                                            key={job.id}
                                            job={job}
                                            isSelected={selectedJobIds.includes(job.id)}
                                            hasSelection={selectedJobIds.length > 0}
                                            onSelectToggle={handleToggleSelectJob}
                                            onView={handleView}
                                            onEdit={handleEdit}
                                            onDelete={handleDelete}
                                            onToggleStatus={handleToggleStatus}
                                        />
                                    ))}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ── Status Warning Modal ── */}
            <Modal
                show={statusWarningModal.show}
                onHide={() => setStatusWarningModal({ show: false, title: '', message: '', publishedJobIds: [] })}
                centered
                contentClassName="border-0 shadow"
                style={{ '--bs-modal-bg': '#ffffff', '--bs-modal-border-radius': '12px' } as React.CSSProperties}
            >
                <Modal.Header closeButton className="border-0 pb-0" />
                <Modal.Body className="text-center px-3 pb-3 pt-0">
                    <div style={{ margin: '0 auto 12px', background: '#fef2f2', border: '1px solid #fee2e2', width: 52, height: 52, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <XCircle size={26} color="#dc2626" />
                    </div>
                    <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6, fontSize: 17 }}>
                        {statusWarningModal.title}
                    </h4>
                    <p style={{ color: '#475569', fontSize: 13, marginBottom: (statusWarningModal.jobNames?.length || statusWarningModal.deadlineInfo) ? 14 : 18 }}>
                        {statusWarningModal.message}
                    </p>
                    {statusWarningModal.expiredJobs && statusWarningModal.expiredJobs.length > 0 ? (
                        <div className="mb-3">
                            <div style={{ color: '#1e293b', fontWeight: 600, fontSize: 13, marginBottom: 8, textAlign: 'left' }}>
                                Jobs requiring new deadlines:
                            </div>
                            <div className="d-flex flex-column gap-2 text-start">
                                {statusWarningModal.expiredJobs.map(job => {
                                    const isPublished = statusWarningModal.publishedJobIds?.includes(job.id);
                                    return (
                                        <div key={job.id} className="d-flex align-items-center justify-content-between p-2 px-3 rounded" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)', opacity: isPublished ? 0.6 : 1 }}>
                                            <div style={{ overflow: 'hidden', paddingRight: '8px' }}>
                                                <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.title}</div>
                                                <div style={{ fontSize: 11.5, color: '#ef4444', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }}></div>
                                                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        Expired: {job.application_deadline ? new Date(job.application_deadline).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm shadow-none"
                                                    min={new Date().toISOString().split('T')[0]}
                                                    value={inlineDeadlines[job.id] || ''}
                                                    onChange={(e) => setInlineDeadlines(prev => ({ ...prev, [job.id]: e.target.value }))}
                                                    style={{ width: 120, fontSize: 12, height: 28, borderColor: '#cbd5e1', borderRadius: '6px' }}
                                                    disabled={isPublished}
                                                />
                                                <Button
                                                    variant="light"
                                                    size="sm"
                                                    className="shadow-sm"
                                                    disabled={isUpdatingDeadline.includes(job.id) || !inlineDeadlines[job.id] || isPublished}
                                                    onClick={() => handleUpdateDeadlineAndPublish(job)}
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        padding: '3px 10px',
                                                        height: 28,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: isPublished ? '#f8fafc' : '#ffffff',
                                                        border: '1px solid #cbd5e1',
                                                        color: isPublished ? '#94a3b8' : '#002781',
                                                        borderRadius: '6px',
                                                        transition: 'all 0.2s ease-in-out',
                                                        whiteSpace: 'nowrap',
                                                        width: 'auto',
                                                        minWidth: '65px'
                                                    }}
                                                >
                                                    {isPublished ? 'Published' : isUpdatingDeadline.includes(job.id) ? 'Publishing...' : 'Publish'}
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <>
                            {statusWarningModal.jobNames && statusWarningModal.jobNames.length > 0 && (
                                <div
                                    className="mb-2 py-1.5 px-3 rounded text-center"
                                    style={{ background: '#f8fafc', border: '1px solid #cbd5e1', fontSize: 12.5 }}
                                >
                                    <span style={{ fontWeight: 600, color: '#475569' }}>
                                        {statusWarningModal.jobNames.length === 1 ? 'Job Name:' : 'Job Name(s):'}
                                    </span>{' '}
                                    <span style={{ color: '#002781', fontWeight: 600 }}>
                                        {statusWarningModal.jobNames.join(', ')}
                                    </span>
                                </div>
                            )}
                            {statusWarningModal.deadlineInfo && (
                                <div
                                    className="p-2 mb-3 rounded text-center"
                                    style={{
                                        background: '#fef2f2',
                                        border: '1px solid #fca5a5',
                                        color: '#dc2626',
                                        fontSize: 12.5,
                                        fontWeight: 600,
                                        whiteSpace: 'pre-line',
                                    }}
                                >
                                    {statusWarningModal.deadlineInfo}
                                </div>
                            )}
                        </>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
                        <Button
                            variant="primary"
                            onClick={() => setStatusWarningModal({ show: false, title: '', message: '' })}
                            style={{ minWidth: 120, borderRadius: '8px', background: '#002781', borderColor: '#002781', fontWeight: 600, padding: '6px 20px', fontSize: 13, height: 32 }}
                        >
                            Got It
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* ── Bulk Delete Confirmation Modal ── */}
            <ModalSuccesUpdate
                show={showBulkDeleteModal}
                onClose={() => !isBulkDeleting && setShowBulkDeleteModal(false)}
                icon={
                    <div style={{ margin: '0 auto', background: '#fef2f2', border: '1px solid #fca5a5', width: 70, height: 70, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Trash2 size={36} color="#dc2626" />
                    </div>
                }
                title="Bulk Delete Jobs"
                message={`Are you sure you want to delete the selected ${selectedJobIds.length} job(s)? This action cannot be undone.`}
                buttonText={isBulkDeleting ? "Deleting..." : "Delete Selected"}
                buttonText2="Cancel"
                isDanger
                onPrimaryButtonClick={handleConfirmBulkDelete}
                onSecondButtonClick={() => setShowBulkDeleteModal(false)}
            />

            {/* ── Bulk Close Confirmation Modal ── */}
            <ModalSuccesUpdate
                show={showBulkCloseModal}
                onClose={() => !isBulkClosing && setShowBulkCloseModal(false)}
                icon={
                    <div style={{ margin: '0 auto', background: '#fffbeb', border: '1px solid #fde68a', width: 70, height: 70, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <XCircle size={36} color="#d97706" />
                    </div>
                }
                title="Bulk Close Job Positions"
                message={`Are you sure you want to close the selected ${selectedJobIds.length} job position(s)? These positions will stop accepting new applications.`}
                buttonText={isBulkClosing ? "Closing..." : "Close Positions"}
                buttonText2="Cancel"
                isDanger
                onPrimaryButtonClick={handleConfirmBulkClose}
                onSecondButtonClick={() => setShowBulkCloseModal(false)}
            />

            {/* ── Delete Confirmation Modal ── */}
            <ModalSuccesUpdate
                show={!!jobToDelete}
                onClose={() => !isDeletingJob && setJobToDelete(null)}
                icon={
                    <div style={{ margin: '0 auto', background: '#fef2f2', border: '1px solid #fca5a5', width: 70, height: 70, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Trash2 size={36} color="#dc2626" />
                    </div>
                }
                title="Delete Job Post"
                message={`Are you sure you want to delete "${jobToDelete?.title || 'this job post'}"? This action cannot be undone.`}
                buttonText={isDeletingJob ? "Deleting..." : "Delete"}
                buttonText2="Cancel"
                isDanger
                onPrimaryButtonClick={handleConfirmDelete}
                onSecondButtonClick={() => setJobToDelete(null)}
            />

            {/* ── Delete Success Modal ── */}
            <ModalSuccesUpdate
                show={showDeleteSuccess}
                onClose={() => setShowDeleteSuccess(false)}
                img={succesimage}
                title="Job Post Deleted Successfully"
                message="The job post has been deleted successfully. All associated data has been removed from your account."
                buttonText="Close"
                onPrimaryButtonClick={() => setShowDeleteSuccess(false)}
            />

            {/* Toggle Status Confirmation Modal */}
            <Modal
                show={!!jobToToggle}
                onHide={() => !isToggling && setJobToToggle(null)}
                centered
            >
                <Modal.Header closeButton={!isToggling} className="border-0 pb-0" />

                <Modal.Body className="text-center px-4 pb-4 pt-1">
                    <div style={{ margin: '0 auto 14px', background: '#F8FAFC', border: '1px solid #E2E8F0', width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {Boolean((jobToToggle as any)?.is_draft || jobToToggle?.isClosed || jobToToggle?.is_closed) ? (
                            <Send size={30} color="#15803d" />
                        ) : (
                            <FileText size={30} color="#b45309" />
                        )}
                    </div>

                    <h4 style={{ fontWeight: 700, color: '#1f2937', marginBottom: 8, fontSize: 17 }}>
                        {Boolean((jobToToggle as any)?.is_draft || jobToToggle?.isClosed || jobToToggle?.is_closed)
                            ? 'Publish Job'
                            : 'Move Job to Draft'}
                    </h4>

                    <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 18 }}>
                        Are you sure you want to{' '}
                        <strong>
                            {Boolean((jobToToggle as any)?.is_draft || jobToToggle?.isClosed || jobToToggle?.is_closed) ? 'publish' : 'move to draft'}
                        </strong>{' '}
                        <strong>{jobToToggle?.title || 'this job'}</strong>?
                    </p>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <style>{`
        button.btn.jl-modal-action-btn {
          border-radius: 8px !important;
          padding: 6px 14px !important;
          white-space: nowrap !important;
          height: 34px !important;
          font-size: 12.5px !important;
        }
        button.btn.btn-primary.jl-modal-action-btn {
          background-color: #002781 !important;
          border-color: #002781 !important;
          color: #ffffff !important;
          border-radius: 8px !important;
        }
      `}</style>
                        <Button
                            variant="outline-secondary"
                            className="jl-modal-action-btn"
                            onClick={() => setJobToToggle(null)}
                            disabled={isToggling}
                            style={{ width: 130 }}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="primary"
                            className="jl-modal-action-btn"
                            onClick={handleConfirmToggle}
                            disabled={isToggling}
                            style={{ width: 130 }}
                        >
                            {isToggling ? (
                                <>
                                    <SkeletonLoader
                                        inline
                                        width={14}
                                        height={14}
                                        style={{ borderRadius: 4, marginRight: 6 }}
                                    />
                                    Updating...
                                </>
                            ) : Boolean((jobToToggle as any)?.is_draft || jobToToggle?.isClosed || jobToToggle?.is_closed) ? (
                                'Publish'
                            ) : (
                                'Move to Draft'
                            )}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Bulk Toggle Status Confirmation Modal */}
            <Modal
                show={!!bulkToggleAction}
                onHide={() => !isBulkPublishing && !isBulkDrafting && setBulkToggleAction(null)}
                centered
            >
                <Modal.Header closeButton={!isBulkPublishing && !isBulkDrafting} className="border-0 pb-0" />

                <Modal.Body className="text-center px-4 pb-4 pt-1">
                    <div style={{ margin: '0 auto 14px', background: '#F8FAFC', border: '1px solid #E2E8F0', width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {bulkToggleAction === 'publish' ? (
                            <Send size={30} color="#15803d" />
                        ) : (
                            <FileText size={30} color="#b45309" />
                        )}
                    </div>

                    <h4 style={{ fontWeight: 700, color: '#1f2937', marginBottom: 8, fontSize: 17 }}>
                        {bulkToggleAction === 'publish' ? 'Publish Jobs' : 'Move Jobs to Draft'}
                    </h4>

                    <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 18 }}>
                        Are you sure you want to{' '}
                        <strong>
                            {bulkToggleAction === 'publish' ? 'publish' : 'move to draft'}
                        </strong>{' '}
                        <strong>{selectedJobIds.length}</strong> selected job(s)?
                    </p>

                    <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <Button
                            variant="outline-secondary"
                            className="jl-modal-action-btn"
                            onClick={() => setBulkToggleAction(null)}
                            disabled={isBulkPublishing || isBulkDrafting}
                            style={{ width: 130 }}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="primary"
                            className="jl-modal-action-btn"
                            onClick={handleConfirmBulkToggle}
                            disabled={isBulkPublishing || isBulkDrafting}
                            style={{ width: 130 }}
                        >
                            {(isBulkPublishing || isBulkDrafting) ? (
                                <>
                                    <SkeletonLoader
                                        inline
                                        width={14}
                                        height={14}
                                        style={{ borderRadius: 4, marginRight: 6 }}
                                    />
                                    Updating...
                                </>
                            ) : bulkToggleAction === 'publish' ? (
                                'Publish'
                            ) : (
                                'Move to Draft'
                            )}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* ── Close Position Confirmation Modal ── */}
            <CloseJobModal
                show={!!jobToClose}
                jobTitle={jobToClose?.title}
                isClosing={isClosing}
                onCancel={() => setJobToClose(null)}
                onConfirm={handleConfirmClose}
            />
            {/* Verification Required Modal */}
            <VerificationRequiredModal
                show={showVerificationModal}
                onHide={() => setShowVerificationModal(false)}
            />

            {/* Credit Confirmation Modal */}
            {isCreditPopupOpen && creditPopupOptions && (
                <CreditConfirmPopup
                    isOpen={isCreditPopupOpen}
                    {...creditPopupOptions}
                    onConfirm={handleCreditConfirm}
                    onCancel={handleCreditCancel}
                    onNeverShowAgain={handleCreditNeverShowAgain}
                />
            )}
        </div>
    );
};

export default JobManagement;
